import { autoinject } from "aurelia-dependency-injection";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import {
    GetPortCallSummaryRequest, PortsAndTerminalUrls,
    PortTerminalAndBerthResponse, PortCallSummaryItem,
    BuPortConfigModel, PortCallStatusModel,
    ScreeningRiskModel, PortFormModel, CreateVesselFormsRequest,
    CreateVesselFormsResponse,
    GetVesselPreArrivalRequest,
    GetVesselPreArrivalResponse,
    GetRiskAnalysisRulesRequest,
    GetRiskAnalysisRulesResponse,
    UploadVesselPortCallsRequest,
    GetVesselPortCallAssignmentRequest,
    GetVesselPortCallAssignmentResponse,
    SetVesselPreArrivalAssignmentRequest,
    SetVesselPreArrivalAssignmentResponse,
    PortCallStatusHistoryItemModel,
    SaveBuPortConfigRequest,
    PortCallFormModel,
    GetRiskHistoryRequest,
    GetRiskHistoryResponse
} from "../models/ports-and-terminals.models";
import { CreateOrUpdateVesselPortCallRequest } from "../models/create-vessel-port-arrival-model";
import { NewFbrRequest, NewFbrResponse } from "../../../../../models/fbrs/fbr.models";
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";

@autoinject()
export class PortsAndTerminalsService {

    private _businessUnitId: number;
    private _ptBuCacheKey: string = "pt-bu-setting";

    constructor(private requestClient: RequestClientService, private cacheClient: CacheClientService) {
        this.loadCachedBuSetting();
    }

    public createVesselPortArrival(model: CreateOrUpdateVesselPortCallRequest): Promise<boolean> {
        return this.requestClient.post(PortsAndTerminalUrls.CreateVesselPortArrival, {}, model);
    }

    public updateVesselPortArrival(model: CreateOrUpdateVesselPortCallRequest): Promise<boolean> {
        return this.requestClient.post(PortsAndTerminalUrls.UpdateVesselPortArrival, {}, model);
    }

    public getVesselPreArrival(request: GetVesselPreArrivalRequest): Promise<GetVesselPreArrivalResponse> {
        return new Promise<GetVesselPreArrivalResponse>((resolve, reject) => {
            this.loadCachedBuSetting().then(() => {
                request.businessUnitId = this._businessUnitId;
                this.requestClient.post<GetVesselPreArrivalResponse>(PortsAndTerminalUrls.GetVesselPreArrival, {}, request)
                    .then(resolve, e => reject(e));
            }).catch(reject);
        });
    }

    public getVesselPortCallStatuses(vesselPortCallId: number): Promise<PortCallStatusHistoryItemModel[]> {
        return this.requestClient.get(PortsAndTerminalUrls.GetVesselPortCallStatuses, { vesselPortCallId });
    }

    public loadPortsTerminalsAndBerths(): Promise<PortTerminalAndBerthResponse> {
        return new Promise<PortTerminalAndBerthResponse>((resolve, reject) => {
            this.loadCachedBuSetting().then(() => {
                this.requestClient.get<PortTerminalAndBerthResponse>(PortsAndTerminalUrls.LoadPortsTerminalsAndBerths, { businessUnitId: this._businessUnitId })
                    .then(resolve, e => reject(e));
            }).catch(reject);
        });
    }

    public getPortCallSummaryItems(request: GetPortCallSummaryRequest): Promise<PortCallSummaryItem[]> {
        return new Promise<PortCallSummaryItem[]>((resolve, reject) => {
            this.loadCachedBuSetting().then(() => {
                request.businessUnitId = this._businessUnitId;
                this.requestClient.post<PortCallSummaryItem[]>(PortsAndTerminalUrls.GetPortCallSummaryItems, {}, request)
                    .then(resolve, e => reject(e));
            }).catch(reject);
        });
    }

    public saveBuPortConfig(request: SaveBuPortConfigRequest): Promise<boolean> {
        return this.requestClient.post(PortsAndTerminalUrls.SaveBuPortConfig, {}, request);
    }

    public getBuPortConfig(businessUnitId: number): Promise<BuPortConfigModel[]> {
        return this.requestClient.get(PortsAndTerminalUrls.GetBuPortConfig, { businessUnitId });
    }

    public loadPortCallStatus(): Promise<PortCallStatusModel[]> {
        return this.requestClient.get(PortsAndTerminalUrls.LoadPortCallStatuses);
    }

    public loadScreeningRisks(): Promise<ScreeningRiskModel[]> {
        return this.requestClient.get(PortsAndTerminalUrls.LoadScreeningRisks);
    }

    public getPortForms(portSKey: number): Promise<PortFormModel[]> {
        return new Promise<PortFormModel[]>((resolve, reject) => {
            this.loadCachedBuSetting().then(() => {
                this.requestClient.get<PortFormModel[]>(PortsAndTerminalUrls.GetPortForms, { businessUnitId: this._businessUnitId, portSKey: portSKey })
                    .then(resolve, e => reject(e));
            }).catch(reject);
        });
    }

    public createVesselForms(request: CreateVesselFormsRequest): Promise<CreateVesselFormsResponse> {
        return new Promise<CreateVesselFormsResponse>((resolve, reject) => {
            this.loadCachedBuSetting().then(() => {
                request.businessUnitId = this._businessUnitId;
                this.requestClient.post(PortsAndTerminalUrls.CreateVesselForms, {}, request)
                    .then(resolve, e => reject(e));
            }).catch(reject);
        });
    }

    public getValidVesselPortCallForms(vesselPortCallId: number, businessUnitId: number): Promise<PortCallFormModel[]> {
        return this.requestClient.get(PortsAndTerminalUrls.GetValidVesselPortCallForms, { vesselPortCallId, businessUnitId });
    }

    public getRiskHistory(request: GetRiskHistoryRequest): Promise<GetRiskHistoryResponse> {
        return this.requestClient.post(PortsAndTerminalUrls.GetRiskHistory, {}, request);
    }

    public getRiskAnalysisRules(request: GetRiskAnalysisRulesRequest): Promise<GetRiskAnalysisRulesResponse> {
        return this.requestClient.post(PortsAndTerminalUrls.GetRiskAnalysisRules, {}, request);
    }

    public getVesselPortCallAssignment(request: GetVesselPortCallAssignmentRequest): Promise<GetVesselPortCallAssignmentResponse> {
        return this.requestClient.get(PortsAndTerminalUrls.GetVesselPortCallAssignment, request);
    }

    public setVesselPortCallAssignment(request: SetVesselPreArrivalAssignmentRequest): Promise<SetVesselPreArrivalAssignmentResponse> {
        return this.requestClient.post(PortsAndTerminalUrls.SetVesselPortCallAssignment, {}, request);
    }

    public changeVesselPortCallStatus(vesselPortCallId: number, statusId: number, comments: string): Promise<void> {
        return this.requestClient.post(PortsAndTerminalUrls.ChangeVesselPortCallStatus, {}, {
            vesselPortCallId: vesselPortCallId,
            comments: comments,
            statusId: statusId
        });
    }

    public uploadVesselPortCalls(request: UploadVesselPortCallsRequest): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.loadCachedBuSetting().then(() => {
                request.businessUnitId = this._businessUnitId;
                this.requestClient.postFormData(PortsAndTerminalUrls.UploadVesselPortCalls, request)
                    .then(resolve, e => reject(e));
            }).catch(reject);
        });
    }

    submitFbr(request: NewFbrRequest): Promise<NewFbrResponse> {
        return this.requestClient.postFormData(PortsAndTerminalUrls.SubmitFbr, request);
    }

    public setBusinessUnit(businessUnitId: number) {
        this._businessUnitId = businessUnitId;
    }

    public async loadCachedBuSetting(): Promise<number> {
        if (!this._businessUnitId) {
            const response = await this.cacheClient.get<{ customerId: number, businessUnitId: number }>(this._ptBuCacheKey, CacheLocation.LocalStorage, false);
            if (response) {
                this._businessUnitId = response.businessUnitId;
            }
            return this._businessUnitId;
        }
        return this._businessUnitId;
    }

    public getPortOffset(portSKey: number): Promise<string> {
        return this.requestClient.get(PortsAndTerminalUrls.GetPortOffset, { portSKey });
    }
}
